import { appAlert } from '@/lib/appAlert';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@/lib/functions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useOnAppActive } from '@/hooks/useOnAppActive';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ProfileForm, type ProfileFormHandle } from '@/components/ProfileForm';
import { regionMatchesQuery } from '@/lib/regionTowns';
import { supabase } from '@/lib/supabase';
import { ReviewSummaryCard } from '@/components/ReviewSummaryCard';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { LevelBadge, type LevelInfo } from '@/components/ui/LevelBadge';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { UserIdChip } from '@/components/ui/UserIdChip';
import { SignaturePad, type SignatureData } from '@/components/ui/SignaturePad';
import { SignatureView } from '@/components/ui/SignatureView';
import { contentMaxWidth, fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { TabScreenWrapper } from '@/components/TabScreenWrapper';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { ProfileTour, PROFILE_TOUR_KEY } from '@/components/ProfileTour';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import type { ReviewSummary } from '@/lib/database.types';
import { fetchPublicProfilesByIds } from '@/lib/agreements';
import { effectiveAvatarUrl, pickAndUploadAvatar } from '@/lib/avatar';
import { formatDate, fullNameMissingAccents, toTitleCase } from '@/lib/format';
import { getUserLevel } from '@/lib/levels';
import { fetchMyReliabilityPercent, fetchReviewsFor, getReviewSummary, type ReviewWithStatus } from '@/lib/reviews';
import { fetchDisputesForProfile, type DisputeOutcomeEntry } from '@/lib/disputes';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { countPendingIdentityVerifications } from '@/lib/identityVerification';
import { countPendingVerifications } from '@/lib/verification';

function InfoButton({ message }: { message: string }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <Pressable
      onPress={() => appAlert(t('common.info'), message)}
      hitSlop={10}
      accessibilityRole="button"
    >
      <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
    </Pressable>
  );
}

type VerificationsCardProps = {
  identityVerified: boolean;
  identityPending: boolean;
  onVerifyIdentity: () => void;
  colors: AppColors;
};

function VerificationsCard({ identityVerified, identityPending, onVerifyIdentity, colors }: VerificationsCardProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderLeft}>
          <Ionicons name="shield-checkmark-outline" size={16} color={colors.primary} />
          <Text style={styles.sectionTitle}>{t('profile.section_verifications')}</Text>
        </View>
      </View>

      {/* Επαλήθευση ταυτότητας — μοναδικός δρόμος πλέον προς το 100% αξιοπιστίας
          (η επαλήθευση κινητού με SMS καταργήθηκε, μη αξιόπιστη εκτός Ελλάδας). */}
      <Pressable
        style={[
          styles.verifyRow,
          identityVerified && styles.verifyRowDone,
          identityPending && !identityVerified && styles.verifyRowPending,
        ]}
        onPress={identityVerified || identityPending ? undefined : onVerifyIdentity}
        accessibilityRole="button"
        disabled={identityVerified || identityPending}
      >
        <View style={[
          styles.verifyStep,
          identityVerified && styles.verifyStepDone,
          identityPending && !identityVerified && styles.verifyStepPending,
        ]}>
          {identityVerified
            ? <Ionicons name="checkmark" size={14} color="#fff" />
            : identityPending
              ? <Ionicons name="time-outline" size={13} color="#fff" />
              : <Ionicons name="id-card-outline" size={13} color="#fff" />
          }
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[
            styles.verifyRowTitle,
            identityVerified && { color: colors.success },
            identityPending && !identityVerified && { color: '#F59E0B' },
          ]}>
            {t('profile.verify_identity_title')}
          </Text>
          <Text style={styles.verifyRowSub}>
            {identityVerified
              ? t('profile.verify_done')
              : identityPending
                ? t('profile.verify_identity_pending')
                : t('profile.verify_identity_sub')}
          </Text>
        </View>
        {!identityVerified && !identityPending && (
          <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
        )}
      </Pressable>
    </View>
  );
}

function cleanIbans(rows: { bank: string; iban: string }[]): { bank: string | null; iban: string }[] {
  return rows
    .map((r) => ({ bank: r.bank.trim() || null, iban: r.iban.trim() }))
    .filter((r) => !!r.iban);
}

export default function ProfileScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { user } = useAuth();
  const { profile, save, refresh } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { tour, section } = useLocalSearchParams<{ tour?: string; section?: string }>();
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [pendingVerifications, setPendingVerifications] = useState(0);

  // ── Περιοχή/νομός — ώστε το σύστημα να ξέρει πού βρίσκεται ο χρήστης και
  // να προτείνει αυτόματα κοντινούς επαγγελματίες (βλ. "sonar" στη Βρες
  // επαγγελματίες), ανεξάρτητα από το αν έχει επαγγελματικό λογαριασμό. ──
  const [region, setRegion] = useState<string | null>((profile as any)?.region ?? null);
  const [regions, setRegions] = useState<string[]>([]);
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [regionQuery, setRegionQuery] = useState('');
  const [regionSaving, setRegionSaving] = useState(false);

  useEffect(() => {
    setRegion((profile as any)?.region ?? null);
  }, [(profile as any)?.region]);

  useEffect(() => {
    void supabase.from('greek_regions').select('name').order('name')
      .then(({ data }) => { if (data) setRegions((data as { name: string }[]).map((r) => r.name)); });
  }, []);

  const filteredRegions = useMemo(() => {
    return regionQuery.trim() ? regions.filter((r) => regionMatchesQuery(r, regionQuery)) : regions;
  }, [regions, regionQuery]);

  async function selectRegion(name: string | null) {
    setRegion(name);
    setShowRegionPicker(false);
    setRegionQuery('');
    setRegionSaving(true);
    try { await save({ region: name } as any); }
    catch { /* silent */ }
    finally { setRegionSaving(false); }
  }

  useEffect(() => {
    if (!profile?.is_admin) return;
    void Promise.all([countPendingIdentityVerifications(), countPendingVerifications()])
      .then(([a, b]) => setPendingVerifications(a + b))
      .catch(() => {});
  }, [profile?.is_admin]);
  const [showTour, setShowTour] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const sigSectionRef = useRef<View>(null);
  const [formDirty, setFormDirty] = useState(false);
  const floatAnim = useRef(new Animated.Value(0)).current;
  const profileFormRef = useRef<ProfileFormHandle>(null);
  const levelProgressAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(floatAnim, {
      toValue: formDirty ? 1 : 0,
      duration: 260,
      easing: Easing.out(Easing.back(1.4)),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [formDirty, floatAnim]);

  useEffect(() => {
    if (tour !== '1') return;
    let mounted = true;
    void AsyncStorage.getItem(PROFILE_TOUR_KEY).then((v) => {
      if (mounted && !v) setShowTour(true);
    });
    return () => { mounted = false; };
  }, [tour]);


  useEffect(() => {
    if (section !== 'signature') return;
    setSigExpanded(true);
    // Μικρή καθυστέρηση για να φορτωθεί το layout πριν το scroll
    const t = setTimeout(() => {
      sigSectionRef.current?.measureLayout(
        scrollRef.current as never,
        (_x, y) => scrollRef.current?.scrollTo({ y: y - 16, animated: true }),
        () => {},
      );
    }, 400);
    return () => clearTimeout(t);
  }, [section]);

  useEffect(() => {
    if (section !== 'professionalTerms') return;
    router.push('/(app)/professional-account' as any);
  }, [section, router]);

  async function changeAvatar() {
    if (!user) return;
    setAvatarBusy(true);
    try {
      const url = await pickAndUploadAvatar(user.id);
      if (url) await refresh();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_uploadFailed')));
    } finally {
      setAvatarBusy(false);
    }
  }

  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [reviews, setReviews] = useState<ReviewWithStatus[]>([]);
  const [reviewerNames, setReviewerNames] = useState<Record<string, string>>({});
  const [reviewerNicknames, setReviewerNicknames] = useState<Record<string, string | null>>({});
  const [reviewerReliability, setReviewerReliability] = useState<Record<string, number | null>>({});
  const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);
  const [disputeOutcomes, setDisputeOutcomes] = useState<DisputeOutcomeEntry[]>([]);

  const { refresh: refreshBadge } = useNeedsAction();

  const [drawn, setDrawn] = useState<SignatureData | null>(null);
  const [sigTouched, setSigTouched] = useState(false);
  const [sigEditing, setSigEditing] = useState(false);
  const [sigExpanded, setSigExpanded] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const [activeAgreementsLocked, setActiveAgreementsLocked] = useState(false);
  const [identityPending, setIdentityPending] = useState(false);

  const [pendingName, setPendingName] = useState(profile?.full_name ?? '');
  const [nameSaving, setNameSaving] = useState(false);

  const [pendingNickname, setPendingNickname] = useState((profile as any)?.nickname ?? '');
  const [nicknameSaving, setNicknameSaving] = useState(false);
  const [showNicknameField, setShowNicknameField] = useState(!!(profile as any)?.nickname?.trim());
  const nicknameUpdatedAt = (profile as any)?.nickname_updated_at ? new Date((profile as any).nickname_updated_at) : null;
  const nicknameDaysRemaining = nicknameUpdatedAt
    ? Math.max(0, 30 - Math.floor((Date.now() - nicknameUpdatedAt.getTime()) / 86400000))
    : 0;
  const nicknameLocked = nicknameDaysRemaining > 0;

  async function saveNicknameOnBlur() {
    if (nicknameLocked) return;
    const trimmed = pendingNickname.trim();
    if (trimmed === ((profile as any)?.nickname ?? '')) return;
    setNicknameSaving(true);
    try { await save({ nickname: trimmed || null } as any); }
    catch { /* silent */ }
    finally { setNicknameSaving(false); }
  }

  const [commApps, setCommApps] = useState<string[]>(
    () => (profile?.communication_apps as string[] | undefined) ?? [],
  );
  async function toggleCommApp(app: string) {
    const next = commApps.includes(app) ? commApps.filter((a) => a !== app) : [...commApps, app];
    setCommApps(next);
    try { await save({ communication_apps: next }); } catch { /* silent */ }
  }

  // Το όνομα κλειδώνει μετά από έγκριση ταυτότητας (full_name_locked) ή όσο
  // υπάρχει έστω μία μη-τερματική συμφωνία (activeAgreementsLocked) — και στις
  // δύο περιπτώσεις δεν πρέπει να είναι πλέον επεξεργάσιμο από τον χρήστη.
  const nameLocked = !!(profile as any)?.full_name_locked || activeAgreementsLocked;

  async function saveNameOnBlur() {
    if (nameLocked) return;
    const trimmed = toTitleCase(pendingName);
    if (!trimmed || trimmed === profile?.full_name) return;
    setPendingName(trimmed);
    setNameSaving(true);
    try { await save({ full_name: trimmed }); }
    catch { /* silent */ }
    finally { setNameSaving(false); }
  }

  async function saveSignature() {
    setSavingSig(true);
    try {
      await save({ signature_svg: drawn ? JSON.stringify(drawn) : null });
      setSigTouched(false);
      setSigEditing(false);
      setSigExpanded(false);
      appAlert(t('profile.alert_profileSaved_title'), drawn ? t('profile.alert_signatureSaved') : t('profile.alert_signatureRemoved'));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('profile.error_saveFailed')));
    } finally {
      setSavingSig(false);
    }
  }

  useEffect(() => {
    if (!levelInfo) return;
    const ratio = levelInfo.required_next > levelInfo.required_current
      ? Math.min(
          (levelInfo.completed - levelInfo.required_current) /
          (levelInfo.required_next - levelInfo.required_current),
          1,
        )
      : 1;
    Animated.timing(levelProgressAnim, {
      toValue: Math.max(0, ratio),
      duration: 700,
      useNativeDriver: false,
    }).start();
  }, [levelInfo, levelProgressAnim]);

  const refreshProfileData = useCallback(() => {
    if (!user) return;
    void refresh();
    void getReviewSummary(user.id).then(setSummary).catch(() => setSummary(null));
    void fetchReviewsFor(user.id).then(async (r) => {
      setReviews(r);
      const profiles = await fetchPublicProfilesByIds(r.map((x) => x.reviewer_id));
      setReviewerNames(Object.fromEntries(
        Object.entries(profiles).map(([k, v]) => [k, v.full_name?.trim() || t('common.user')])
      ));
      setReviewerNicknames(Object.fromEntries(
        Object.entries(profiles).map(([k, v]) => [k, (v as any).nickname ?? null])
      ));
      const reliabilityEntries = await Promise.all(
        Object.entries(profiles).map(async ([id, v]) => [
          id,
          await fetchMyReliabilityPercent(id, (v as any).phone_verified, !!(v as any).identity_verified_at).catch(() => null),
        ] as const)
      );
      setReviewerReliability(Object.fromEntries(reliabilityEntries));
    }).catch(() => {});
    void getUserLevel().then(setLevelInfo).catch(() => setLevelInfo(null));
    void fetchDisputesForProfile(user.id).then(setDisputeOutcomes).catch(() => {});
    void supabase
      .from('agreements')
      .select('id', { count: 'exact', head: true })
      .or(`creator_id.eq.${user.id},counterparty_id.eq.${user.id}`)
      .not('status', 'in', '(completed,terminated,cancelled,cancelled_mutual,cancelled_unilateral)')
      .then(({ count }) => setActiveAgreementsLocked((count ?? 0) > 0));
    void (async () => {
      try {
        const { data } = await supabase
          .from('identity_verifications')
          .select('status')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        setIdentityPending(data?.status === 'pending');
      } catch {
        setIdentityPending(false);
      }
    })();
  }, [user, refresh]);

  useFocusEffect(useCallback(() => { refreshProfileData(); }, [refreshProfileData]));
  useOnAppActive(refreshProfileData);

  const hasSig = !!profile?.signature_svg;
  const sigLocked = !!(profile?.signature_locked || activeAgreementsLocked);

  return (
    <TabScreenWrapper>
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        ref={scrollRef}
        style={styles.scrollFlex}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + spacing.lg, paddingBottom: spacing.lg }]}
        keyboardShouldPersistTaps="handled"
      >
        {!!profile?.portfolio_enabled && (
          <View style={styles.profileModeTabs}>
            <View style={[styles.profileModeTab, styles.profileModeTabActive]}>
              <Ionicons name="person-outline" size={15} color={colors.textOnPrimary} />
              <Text style={[styles.profileModeTabText, styles.profileModeTabTextActive]}>{t('profile.tab_personal')}</Text>
            </View>
            <Pressable
              style={styles.profileModeTab}
              onPress={() => router.push('/(app)/professional-account' as any)}
              accessibilityRole="button"
            >
              <Ionicons name="briefcase-outline" size={15} color={colors.textMuted} />
              <Text style={styles.profileModeTabText}>{t('profile.tab_professional')}</Text>
            </Pressable>
          </View>
        )}

        {/* ── Avatar + γρανάζι ── */}
        <View style={styles.avatarSection}>
          {!!profile?.is_admin && (
            <Pressable
              style={styles.adminBtn}
              onPress={() => router.push('/(app)/admin/verifications')}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel={t('profile.adminControls')}
            >
              <Ionicons name="shield-checkmark-outline" size={20} color={colors.primary} />
              <Text style={styles.adminBtnText}>{t('profile.adminControls')}</Text>
              {pendingVerifications > 0 && (
                <View style={styles.adminBadge}>
                  <Text style={styles.adminBadgeText}>{pendingVerifications > 9 ? '9+' : pendingVerifications}</Text>
                </View>
              )}
            </Pressable>
          )}
          <Pressable
            style={styles.settingsBtn}
            onPress={() => router.push('/(app)/settings')}
            hitSlop={10}
            accessibilityRole="button"
          >
            <Ionicons name="settings-outline" size={22} color={colors.textMuted} />
          </Pressable>
          <View style={styles.avatarWrap}>
            <Pressable onPress={changeAvatar} disabled={avatarBusy} accessibilityRole="button" accessibilityLabel={t('profile.avatar_change')}>
              <Avatar uri={effectiveAvatarUrl(profile)} name={profile?.full_name} size={88} showCameraPrompt={!effectiveAvatarUrl(profile)} />
              <View style={styles.avatarEditOverlay}>
                <Ionicons name="camera" size={15} color="#fff" />
              </View>
            </Pressable>
            {(!!profile?.verified || !!levelInfo || !!profile?.portfolio_enabled) && (
              <View style={styles.badgesOverlay}>
                {!!profile?.verified && <VerifiedBadge compact />}
                {!!profile?.portfolio_enabled && <ProviderBadge compact />}
                {levelInfo && (
                  <LevelBadge
                    info={levelInfo}
                    size="md"
                    memberSince={profile?.created_at ? formatDate(profile.created_at) : undefined}
                    avgRating={summary?.effective_rating ?? null}
                  />
                )}
              </View>
            )}
          </View>
          {avatarBusy && <Text style={styles.avatarAction}>{t('common.uploading')}</Text>}
        </View>

        {/* ── Όνομα + info ── */}
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <TextInput
              style={[styles.nameInput, nameSaving && styles.nameInputSaving]}
              value={pendingName}
              onChangeText={setPendingName}
              onBlur={() => void saveNameOnBlur()}
              placeholder={t('placeholder.yourName')}
              placeholderTextColor="rgba(255,255,255,0.3)"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="done"
              editable={!nameSaving && !nameLocked}
              multiline
              blurOnSubmit
            />
            <InfoButton message={t('profile.info_fullName')} />
          </View>
          <Text style={styles.nameHint}>
            {nameLocked
              ? t((profile as any)?.full_name_locked ? 'profile.nameLockHint' : 'profile.nameLockHintActiveAgreement')
              : t('profile.hint_fullName')}
          </Text>
          {!nameLocked && fullNameMissingAccents(pendingName) && (
            <Text style={styles.nameAccentHint}>{t('profile.nameAccentHint')}</Text>
          )}

          <Pressable
            style={styles.nicknameCheckboxRow}
            onPress={() => setShowNicknameField((v) => !v)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: showNicknameField }}
          >
            <View style={[styles.checkbox, showNicknameField && styles.checkboxOn]}>
              {showNicknameField && <Ionicons name="checkmark" size={14} color="#fff" />}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.checkboxLabel}>{t('profile.nicknameCheckboxLabel')}</Text>
              <Text style={styles.checkboxSublabel}>{t('profile.nicknameCheckboxSublabel')}</Text>
            </View>
          </Pressable>
          {showNicknameField && (
            <>
              <TextInput
                style={[styles.nicknameInput, nicknameSaving && styles.nameInputSaving]}
                value={pendingNickname}
                onChangeText={setPendingNickname}
                onBlur={() => void saveNicknameOnBlur()}
                placeholder={t('profile.placeholder_nickname')}
                placeholderTextColor="rgba(255,255,255,0.3)"
                autoCapitalize="words"
                autoCorrect={false}
                returnKeyType="done"
                maxLength={30}
                editable={!nicknameSaving && !nicknameLocked}
              />
              <Text style={styles.nameHint}>{t('profile.hint_nickname')}</Text>
              {nicknameLocked ? (
                <Text style={styles.nameHint}>
                  {t('profile.nicknameLockHint', { count: nicknameDaysRemaining })}
                </Text>
              ) : pendingNickname.trim() !== ((profile as any)?.nickname ?? '').trim() && (
                <Text style={styles.nicknameWarning}>{t('profile.nicknameWillLockWarning')}</Text>
              )}
            </>
          )}

          <Text style={styles.email}>{user?.email}</Text>
          {!!profile && <UserIdChip userNumber={profile.user_number} />}
          {levelInfo && levelInfo.level < 100 && (
            <View style={styles.levelProgress}>
              <View style={styles.levelProgressTrack}>
                <Animated.View
                  style={[styles.levelProgressFill, {
                    width: levelProgressAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
                  }]}
                />
              </View>
              <Text style={styles.levelProgressText}>
                {t('profile.levelProgress', { completed: levelInfo.completed, required: levelInfo.required_next, level: levelInfo.level + 1 })}
              </Text>
            </View>
          )}
        </View>

        {/* ── Αξιολογήσεις ── */}
        {summary && (
          <ReviewSummaryCard
            summary={summary}
            reviews={reviews}
            reviewerNames={reviewerNames}
            reviewerNicknames={reviewerNicknames}
            reviewerReliability={reviewerReliability}
            phoneVerified={!!profile?.phone_verified}
            identityVerified={!!profile?.verified}
            disputeOutcomes={disputeOutcomes}
          />
        )}

        {/* ── Επαληθεύσεις (κοινό πλακίδιο) ── */}
        {!!profile && (
          <VerificationsCard
            identityVerified={!!profile.verified}
            identityPending={identityPending && !profile.verified}
            onVerifyIdentity={() => router.push('/(app)/identity-verify')}
            colors={colors}
          />
        )}

        {/* ── Υπογραφή (collapsible) ── */}
        {!!profile && (
          <View ref={sigSectionRef} style={styles.card}>
            <Pressable
              style={styles.cardHeader}
              onPress={() => { if (!sigLocked) setSigExpanded((v) => !v); }}
              accessibilityRole="button"
            >
              <View style={styles.cardHeaderLeft}>
                <Text style={styles.sectionTitle}>{t('profile.signature')}</Text>
                {hasSig && !sigLocked && (
                  <View style={styles.sigDot} />
                )}
              </View>
              <View style={styles.cardHeaderRight}>
                <InfoButton message={t('profile.info_signature')} />
                {!sigLocked && (
                  <Ionicons name={sigExpanded ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textMuted} />
                )}
              </View>
            </Pressable>

            {sigLocked ? (
              <>
                {hasSig && (
                  <View style={styles.savedSig}>
                    <SignatureView data={profile.signature_svg!} width={180} />
                  </View>
                )}
                <View style={styles.lockBanner}>
                  <Ionicons name="lock-closed-outline" size={15} color={colors.textMuted} />
                  <Text style={styles.lockText}>
                    {activeAgreementsLocked
                      ? t('profile.sigLockedActive')
                      : t('profile.sigLockedUsed')}
                  </Text>
                </View>
              </>
            ) : sigExpanded ? (
              !sigEditing && hasSig ? (
                <>
                  <View style={styles.savedSig}>
                    <SignatureView data={profile.signature_svg!} width={180} />
                  </View>
                  <Button
                    title={t('profile.action_newSignature')}
                    icon="create-outline"
                    variant="secondary"
                    onPress={() => { setDrawn(null); setSigTouched(false); setSigEditing(true); }}
                  />
                </>
              ) : (
                <>
                  {section === 'signature' && !hasSig && (
                    <View style={styles.sigWelcomeBanner}>
                      <Text style={styles.sigWelcomeText}>{t('profile.sigWelcomeBanner')}</Text>
                    </View>
                  )}
                  <SignaturePad onChange={(d) => { setDrawn(d); setSigTouched(true); }} />
                  <Button
                    title={sigTouched && !drawn ? t('profile.action_removeSignature') : t('common.save')}
                    icon="checkmark-outline"
                    loading={savingSig}
                    disabled={!sigTouched}
                    danger={sigTouched && !drawn}
                    onPress={saveSignature}
                  />
                  {hasSig && (
                    <Pressable onPress={() => { setSigEditing(false); }} hitSlop={8} style={styles.cancelLink}>
                      <Text style={styles.cancelLinkText}>{t('common.cancel')}</Text>
                    </Pressable>
                  )}
                </>
              )
            ) : (
              <Pressable onPress={() => setSigExpanded(true)} style={styles.sigPreviewRow}>
                {hasSig ? (
                  <>
                    <SignatureView data={profile.signature_svg!} width={120} />
                    <Text style={styles.sigEditHint}>{t('common.tapToChange')}</Text>
                  </>
                ) : (
                  <View style={styles.sigAddBtn}>
                    <Ionicons name="add-circle-outline" size={16} color={colors.primary} />
                    <Text style={styles.sigAddBtnText}>{t('profile.action_addSignature')}</Text>
                  </View>
                )}
              </Pressable>
            )}
          </View>
        )}

        {/* ── Στοιχεία προφίλ (τηλέφωνο, τρόποι επικοινωνίας, bio) ── */}
        <ProfileForm
          ref={profileFormRef}
          initial={profile}
          hideSubmitButton
          hideName
          phoneVerified={!!profile?.phone_verified}
          identityVerified={!!profile?.verified}
          commApps={commApps}
          onCommAppToggle={toggleCommApp}
          onDirtyChange={setFormDirty}
          onSubmit={async (patch) => {
            await save(patch);
            setFormDirty(false);
            appAlert(t('profile.alert_profileSaved_title'), t('profile.alert_profileSaved_body'));
          }}
        />

        {/* ── Περιοχή/νομός ── */}
        {!!profile && (
          <Pressable
            style={styles.card}
            onPress={() => setShowRegionPicker(true)}
            accessibilityRole="button"
          >
            <View style={styles.cardHeader}>
              <View style={styles.cardHeaderLeft}>
                <Ionicons name="location-outline" size={16} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('profile.regionSectionTitle')}</Text>
              </View>
              <View style={styles.cardHeaderRight}>
                {regionSaving ? <ActivityIndicator size="small" color={colors.primary} /> : null}
                <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
              </View>
            </View>
            <Text style={region ? styles.regionValueText : styles.regionEmptyText}>
              {region || t('profile.regionNotSet')}
            </Text>
          </Pressable>
        )}

        {/* ── Λογαριασμός Επαγγελματία ── */}
        {!!profile && (
          <Pressable
            style={styles.card}
            onPress={() => router.push('/(app)/professional-account' as any)}
            accessibilityRole="button"
          >
            <View style={styles.cardHeader}>
              <View style={styles.cardHeaderLeft}>
                <Ionicons name="briefcase-outline" size={16} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('profile.section_professional')}</Text>
                <View style={[styles.activePill, !profile?.portfolio_enabled && styles.inactivePill]}>
                  <Text style={[styles.activePillText, !profile?.portfolio_enabled && styles.inactivePillText]}>
                    {profile?.portfolio_enabled ? t('common.active') : t('common.inactive')}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
            </View>
          </Pressable>
        )}
      </ScrollView>
      {/* Floating save button — εμφανίζεται όταν υπάρχουν αλλαγές στη φόρμα */}
      <Animated.View
        style={[
          styles.floatBtn,
          { bottom: insets.bottom + 16 },
          {
            transform: [{
              translateY: floatAnim.interpolate({ inputRange: [0, 1], outputRange: [100, 0] }),
            }],
            opacity: floatAnim,
          },
        ]}
        pointerEvents={formDirty ? 'auto' : 'none'}
      >
        <Pressable
          style={styles.floatBtnInner}
          onPress={async () => {
            if (formDirty) await profileFormRef.current?.submit();
          }}
          accessibilityRole="button"
        >
          <Text style={styles.floatBtnIcon}>💾</Text>
          <Text style={styles.floatBtnText}>{t('profile.action_saveChanges')}</Text>
        </Pressable>
      </Animated.View>

      {showTour && <ProfileTour onDone={() => setShowTour(false)} />}

      {showRegionPicker && (
        <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={() => setShowRegionPicker(false)}>
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <Pressable style={styles.regionModalOverlay} onPress={() => setShowRegionPicker(false)}>
            <Pressable style={styles.regionModalCard} onPress={() => {}}>
              <Text style={styles.regionModalTitle}>{t('profile.regionSectionTitle')}</Text>
              <View style={styles.regionSearchWrap}>
                <Ionicons name="search" size={15} color={colors.textMuted} />
                <TextInput
                  style={styles.regionSearchInput}
                  value={regionQuery}
                  onChangeText={setRegionQuery}
                  placeholder={t('profile.region_search_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {regionQuery.length > 0 && (
                  <Pressable onPress={() => setRegionQuery('')} hitSlop={8}>
                    <Ionicons name="close-circle" size={16} color={colors.textMuted} />
                  </Pressable>
                )}
              </View>
              <ScrollView style={{ maxHeight: 380 }} keyboardShouldPersistTaps="handled">
                {!regionQuery.trim() && (
                  <Pressable style={styles.regionRow} onPress={() => selectRegion(null)}>
                    <Text style={styles.regionRowText}>{t('profile.region_none')}</Text>
                    {region === null && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                  </Pressable>
                )}
                {filteredRegions.map((name) => (
                  <Pressable key={name} style={styles.regionRow} onPress={() => selectRegion(name)}>
                    <Text style={styles.regionRowText}>{name}</Text>
                    {region === name && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                  </Pressable>
                ))}
                {filteredRegions.length === 0 && (
                  <Text style={[styles.regionRowText, { textAlign: 'center', paddingVertical: spacing.md, color: colors.textMuted }]}>
                    {t('profile.region_notFound')}
                  </Text>
                )}
              </ScrollView>
              <Pressable style={styles.regionModalClose} onPress={() => setShowRegionPicker(false)}>
                <Text style={styles.regionModalCloseText}>{t('common.close')}</Text>
              </Pressable>
            </Pressable>
          </Pressable>
          </KeyboardAvoidingView>
        </Modal>
      )}

    </KeyboardAvoidingView>
    </TabScreenWrapper>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    scrollFlex: { flex: 1 },
    regionModalOverlay: {
      flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: spacing.lg,
    },
    regionModalCard: {
      backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.sm,
    },
    regionModalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700', marginBottom: spacing.xs },
    regionSearchWrap: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      paddingHorizontal: spacing.sm, height: 44, marginBottom: spacing.xs,
    },
    regionSearchInput: { flex: 1, color: colors.text, fontSize: fontSize.md },
    regionRow: {
      flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
      paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border,
    },
    regionRowText: { color: colors.text, fontSize: fontSize.md },
    regionModalClose: { alignItems: 'center', paddingVertical: spacing.sm, marginTop: spacing.xs },
    regionModalCloseText: { color: colors.primary, fontSize: fontSize.md, fontWeight: '700' },
    regionValueText: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    regionEmptyText: { color: colors.textMuted, fontSize: fontSize.md },
    content: {
      // Σε πλατιά οθόνη το περιεχόμενο μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    profileModeTabs: {
      flexDirection: 'row',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
    },
    profileModeTab: {
      flex: 1,
      flexDirection: 'row',
      paddingVertical: 10,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },
    profileModeTabActive: { backgroundColor: colors.primary },
    profileModeTabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    profileModeTabTextActive: { color: colors.textOnPrimary },
    avatarSection: {
      alignItems: 'center',
      gap: spacing.xs,
      position: 'relative',
    },
    settingsBtn: {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 10,
      padding: spacing.xs,
    },
    adminBtn: {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 10,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      padding: spacing.xs,
    },
    adminBtnText: {
      color: colors.primary,
      fontSize: fontSize.xs,
      fontWeight: '700',
    },
    adminBadge: {
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      paddingHorizontal: 3,
      backgroundColor: colors.danger,
      alignItems: 'center',
      justifyContent: 'center',
    },
    adminBadgeText: {
      color: '#fff',
      fontSize: 9,
      fontWeight: '800',
    },
    avatarWrap: {
      alignItems: 'center',
      marginBottom: 14,
    },
    avatarEditOverlay: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      backgroundColor: 'rgba(0,0,0,0.55)',
      borderRadius: 12,
      width: 24,
      height: 24,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgesOverlay: {
      position: 'absolute',
      bottom: -14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      justifyContent: 'center',
    },
    avatarAction: {
      color: colors.primaryLight,
      fontSize: fontSize.sm,
      fontWeight: '600',
      marginTop: 4,
    },
    header: {
      gap: spacing.xs,
      alignItems: 'center',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    nameInput: {
      flex: 1,
      color: colors.text,
      textAlign: 'center',
      fontSize: fontSize.xl,
      fontWeight: '700',
      paddingVertical: 2,
      paddingHorizontal: 0,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(201,168,67,0.4)',
    },
    nameInputSaving: { opacity: 0.5 },
    nameHint: {
      color: 'rgba(255,255,255,0.35)',
      fontSize: fontSize.sm,
      marginTop: 2,
    },
    nameAccentHint: {
      color: colors.warning,
      fontSize: fontSize.sm,
      marginTop: 2,
    },
    nicknameCheckboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'center',
      gap: spacing.sm,
      marginTop: spacing.sm,
      maxWidth: '90%',
    },
    checkbox: {
      width: 20, height: 20, borderRadius: 5,
      borderWidth: 1.5, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
    },
    checkboxOn: { backgroundColor: colors.primary, borderColor: colors.primary },
    checkboxLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      flexShrink: 1,
    },
    checkboxSublabel: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      marginTop: 1,
      flexShrink: 1,
    },
    nicknameWarning: {
      color: '#F59E0B',
      fontSize: fontSize.xs,
      lineHeight: 16,
      textAlign: 'center',
    },
    nicknameInput: {
      color: colors.text,
      textAlign: 'center',
      fontSize: fontSize.lg,
      fontWeight: '600',
      paddingVertical: 4,
      paddingHorizontal: 0,
      marginTop: spacing.xs,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(201,168,67,0.4)',
    },
    email: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    levelProgress: { gap: 4, marginTop: spacing.sm },
    levelProgressTrack: {
      height: 4,
      backgroundColor: colors.surfaceAlt,
      marginHorizontal: -spacing.lg,
      borderRadius: radius.full,
      overflow: 'hidden',
    },
    levelProgressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: radius.full,
    },
    levelProgressText: { color: colors.textMuted, fontSize: 11 },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.md,
      ...shadow.card,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      flex: 1,
    },
    cardHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    sectionTitle: {
      color: colors.textMuted,
      fontSize: 13,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    sigDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.success,
    },
    sigPreviewRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: spacing.xs,
    },
    sigPlaceholder: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontStyle: 'italic',
    },
    sigEditHint: {
      color: colors.primaryLight,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    sigAddBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    sigAddBtnText: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    savedSig: {
      alignItems: 'center',
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
    },
    verifyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    verifyRowDone: {
      opacity: 0.75,
    },
    verifyRowPending: {
      opacity: 0.9,
    },
    verifyStepPending: {
      backgroundColor: '#F59E0B',
    },
    verifyStep: {
      width: 26,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    verifyStepDone: {
      backgroundColor: colors.success,
    },
    verifyRowTitle: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    verifyRowSub: {
      color: colors.textMuted,
      fontSize: 12,
      marginTop: 1,
    },
    lockBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    lockText: {
      flex: 1,
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    cancelLink: { alignSelf: 'center', paddingVertical: spacing.xs },
    cancelLinkText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    floatBtn: {
      position: 'absolute',
      left: spacing.lg,
      right: spacing.lg,
      zIndex: 50,
    },
    floatBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 14,
      paddingHorizontal: spacing.xl,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 10,
      elevation: 8,
    },
    floatBtnIcon: {
      fontSize: 18,
    },
    floatBtnText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '800',
    },
    sigWelcomeBanner: {
      backgroundColor: `${colors.primary}18`,
      borderWidth: 1,
      borderColor: `${colors.primary}40`,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      marginBottom: spacing.md,
    },
    sigWelcomeText: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '700',
      textAlign: 'center',
      lineHeight: 20,
    },
    activePill: {
      backgroundColor: colors.success,
      borderRadius: radius.full,
      paddingVertical: 2,
      paddingHorizontal: spacing.sm,
    },
    activePillText: { color: '#0D1B2A', fontSize: 10, fontWeight: '700', letterSpacing: 0.3 },
    inactivePill: { backgroundColor: colors.surfaceAlt, borderWidth: 1, borderColor: colors.border },
    inactivePillText: { color: colors.textMuted },
    toggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: spacing.xs,
      gap: spacing.md,
    },
    toggleLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      flex: 1,
    },
    toggleLabel: { color: colors.text, fontSize: fontSize.md, fontWeight: '500', flex: 1, flexShrink: 1 },
    toggleSublabel: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 1 },
    toggle: {
      width: 44,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.border,
      padding: 3,
      flexShrink: 0,
    },
    toggleOn: { backgroundColor: colors.primary },
    knob: { width: 20, height: 20, borderRadius: 10, backgroundColor: colors.text },
    knobOn: { transform: [{ translateX: 18 }] },
    viewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: spacing.xs,
    },
    viewBtnText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    fieldSection: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
      gap: spacing.xs,
    },
    fieldLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    fieldLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    fieldInput: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      color: colors.text,
      fontSize: fontSize.md,
    },
    ibanRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      marginTop: spacing.sm,
    },
    ibanBankInput: { flex: 1 },
    ibanIbanInput: { flex: 2 },
    ibanRemoveBtn: { padding: 2 },
    ibanAddBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderStyle: 'dashed',
      borderRadius: radius.md,
      paddingVertical: 8,
      marginTop: spacing.sm,
    },
    ibanAddBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    logoPreview: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    logoChangeBtn: {
      paddingVertical: 6,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    logoChangeBtnText: { color: colors.primaryLight, fontSize: fontSize.sm, fontWeight: '600' },
    logoUploadBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 9,
      paddingHorizontal: spacing.md,
      backgroundColor: 'rgba(99,102,241,0.08)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(99,102,241,0.2)',
      alignSelf: 'flex-start',
    },
    logoUploadBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    wizardSetupBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: spacing.xs,
    },
    wizardSetupBtnText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    wizardReviewCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.md,
    },
    wizardReviewTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    wizardReviewRow: { flexDirection: 'row', justifyContent: 'space-between', gap: spacing.sm },
    wizardReviewLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    wizardReviewValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600', flex: 1, textAlign: 'right' },
  });
}
